import * as Cesium from 'cesium';
// Scale-bar helpers for the Print title block. The graphic scale bar is
// resolution-independent: it is drawn in the sheet's pixel space alongside the
// map window, so it scales with the sheet on export and stays correct.
// Ground metres per screen pixel at a given canvas point, via two ellipsoid
// picks 100 px apart. Returns null if either pick misses the globe.
export const computeMetersPerPixelAt = (viewer, x, y) => {
    try {
        const camera = viewer.camera;
        const ellipsoid = Cesium.Ellipsoid.WGS84;
        const p1 = camera.pickEllipsoid(new Cesium.Cartesian2(x - 50, y), ellipsoid);
        const p2 = camera.pickEllipsoid(new Cesium.Cartesian2(x + 50, y), ellipsoid);
        if (!p1 || !p2)
            return null;
        const c1 = Cesium.Cartographic.fromCartesian(p1, ellipsoid);
        const c2 = Cesium.Cartographic.fromCartesian(p2, ellipsoid);
        const geodesic = new Cesium.EllipsoidGeodesic(c1, c2, ellipsoid);
        return geodesic.surfaceDistance / 100;
    }
    catch (_a) {
        return null;
    }
};
// Round down to a "nice" 1/2/5 × 10^n value so the bar reads cleanly.
const niceNumber = (x) => {
    if (x <= 0)
        return 0;
    const exp = Math.floor(Math.log10(x));
    const base = Math.pow(10, exp);
    const f = x / base;
    const nice = f >= 5 ? 5 : f >= 2 ? 2 : 1;
    return nice * base;
};
const fmt = (n) => n >= 1 ? n.toLocaleString('en-US', { maximumFractionDigits: 0 }) : String(n);
const FEET_PER_METER = 3.28084;
const FEET_PER_MILE = 5280;
// Build a graphic scale bar whose pixel length is close to targetPx for the
// given map scale, in a single unit system: imperial (feet → miles) or metric
// (metres → kilometres).
export const computeScaleBar = (metersPerPixel, system = 'imperial', targetPx = 150) => {
    if (!metersPerPixel || !isFinite(metersPerPixel) || metersPerPixel <= 0)
        return null;
    let distMeters;
    let label;
    if (system === 'metric') {
        const targetMeters = metersPerPixel * targetPx;
        if (targetMeters >= 1000) {
            const niceKm = niceNumber(targetMeters / 1000);
            distMeters = niceKm * 1000;
            label = `${fmt(niceKm)} km`;
        }
        else {
            const niceM = niceNumber(targetMeters);
            distMeters = niceM;
            label = `${fmt(niceM)} m`;
        }
    }
    else {
        const targetFeet = metersPerPixel * targetPx * FEET_PER_METER;
        if (targetFeet >= FEET_PER_MILE) {
            const niceMiles = niceNumber(targetFeet / FEET_PER_MILE);
            distMeters = (niceMiles * FEET_PER_MILE) / FEET_PER_METER;
            label = `${fmt(niceMiles)} mi`;
        }
        else {
            const niceFeet = niceNumber(targetFeet);
            distMeters = niceFeet / FEET_PER_METER;
            label = `${fmt(niceFeet)} ft`;
        }
    }
    return { label, widthPx: distMeters / metersPerPixel };
};
